import { call, put, select, takeLatest } from 'typed-redux-saga'
import { errorHandler, gridSlice } from 'Modules'
import { ResponseCatalog, RequestParams } from 'Models'
import { routerActions } from 'connected-react-router'
import { FrontUrls } from 'Constants/FrontUrls'
import {
	LandingsNS,
	LandingsSlice,
	LandingModel,
	landingApi,
	landingsSelectors,
} from 'Pages/admin/landings'
import { FormNames } from 'Constants/FormNames'
import { Deserialize } from 'cerialize'

function* getAll(action: LandingsNS.GetAll) {
	try {
		const { params, clear, callback } = action.payload
		yield* put(gridSlice.actions.setLoading(true))
		if (clear) {
			yield* put(LandingsSlice.actions.clearAll())
		}
		const res = yield* call(landingApi.getAll, params)
		const items = new ResponseCatalog<LandingModel>(res)
		items.entries = items.entries.map((item): LandingModel => Deserialize(item, LandingModel))
		if (!clear) {
			const oldItems = yield* select(landingsSelectors.selectEntries)
			items.entries = [...oldItems, ...items.entries]
		}
		yield* put(LandingsSlice.actions.setAll(items))
		yield* put(LandingsSlice.actions.setRequestParams(params))
		callback?.()
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(gridSlice.actions.setLoading(false))
	}
}

function* getOne(action: LandingsNS.GetOne) {
	try {
		yield* put(LandingsSlice.actions.setIsLoading(true))
		const res = yield* call(landingApi.getOne, action.payload)
		yield* put(LandingsSlice.actions.setOne(res))
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(LandingsSlice.actions.setIsLoading(false))
	}
}

function* createOne(action: LandingsNS.CreateOne) {
	try {
		yield* put(LandingsSlice.actions.setIsLoading(true))
		const res = yield* call(landingApi.createOne, action.payload)
		yield* put(LandingsSlice.actions.setOne(res))
		yield* put(routerActions.push(FrontUrls.admin_panel.landings))
	} catch (error) {
		yield* call(errorHandler, error, FormNames.ADMIN.LANDING)
	} finally {
		yield* put(LandingsSlice.actions.setIsLoading(false))
	}
}

function* updateOne(action: LandingsNS.UpdateOne) {
	try {
		yield* put(LandingsSlice.actions.setIsLoading(true))
		let { data } = action.payload
		if (!action.payload.data.banner) {
			yield* call(landingApi.deleteBanner, action.payload.id)
			data = {
				...data,
				banner: null,
			}
		}
		if (!action.payload.data.sticker) {
			yield* call(landingApi.deleteSticker, action.payload.id)
			data = {
				...data,
				sticker: null,
			}
		}
		const res = yield* call(landingApi.updateOne, action.payload.id, data)
		yield* put(LandingsSlice.actions.setOne(res))
		yield* put(routerActions.push(FrontUrls.admin_panel.landings))
	} catch (error) {
		yield* call(errorHandler, error, FormNames.ADMIN.LANDING)
	} finally {
		yield* put(LandingsSlice.actions.setIsLoading(false))
	}
}

function* deleteOne(action: LandingsNS.DeleteOne) {
	try {
		yield* put(LandingsSlice.actions.setIsLoading(true))
		yield* call(landingApi.deleteOne, action.payload)
		yield* put(LandingsSlice.actions.getAll({ params: new RequestParams(), clear: true }))
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(LandingsSlice.actions.setIsLoading(false))
	}
}

export function* landingsSaga() {
	yield* takeLatest(LandingsSlice.actions.getAll.type, getAll)
	yield* takeLatest(LandingsSlice.actions.getOne.type, getOne)
	yield* takeLatest(LandingsSlice.actions.createOne.type, createOne)
	yield* takeLatest(LandingsSlice.actions.updateOne.type, updateOne)
	yield* takeLatest(LandingsSlice.actions.deleteOne.type, deleteOne)
}
